import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, LoadingState } from '../ui';
import projectService from '../../services/projectService';

const DEFAULTS = {
  min_chat_members: '1000',
  min_post_views: '1000',
  max_root_post_age_hours: '24',
};

const FIELD_LABELS = {
  min_chat_members: 'Minimum chat members',
  min_post_views: 'Minimum root/post views',
  max_root_post_age_hours: 'Maximum root post age (hours)',
};

// Mirrors the server-side bounds in preissueRelevanceController.js.
const FIELD_BOUNDS = {
  min_chat_members: { min: 0, max: 10000000 },
  min_post_views: { min: 0, max: 1000000000 },
  max_root_post_age_hours: { min: 1, max: 8760 },
};

const FIELD_HINTS = {
  min_chat_members: 'Minimum chat member count for plain chat matches. Default 1000, range 0–10000000.',
  min_post_views: 'Minimum views of the root post for post/comment matches. Default 1000, range 0–1000000000.',
  max_root_post_age_hours: 'Reject matches whose root post is older than this many hours. Default 24, range 1–8760.',
};

const REASON_LABELS = {
  low_members: 'low members',
  low_views: 'low views',
  root_too_old: 'root too old',
  unknown_evidence: 'unknown evidence',
};

const REASON_ORDER = ['low_members', 'low_views', 'root_too_old', 'unknown_evidence'];

function draftFromState(state) {
  return {
    enabled: state?.enabled === true,
    values: state?.enabled && state.settings
      ? {
        min_chat_members: String(state.settings.min_chat_members ?? DEFAULTS.min_chat_members),
        min_post_views: String(state.settings.min_post_views ?? DEFAULTS.min_post_views),
        max_root_post_age_hours: String(state.settings.max_root_post_age_hours ?? DEFAULTS.max_root_post_age_hours),
      }
      : { ...DEFAULTS },
  };
}

function validateThresholds(draft) {
  const errors = {};
  const values = {};
  for (const field of Object.keys(FIELD_LABELS)) {
    const trimmed = String(draft.values[field]).trim();
    const { min, max } = FIELD_BOUNDS[field];
    const numeric = /^-?\d+$/.test(trimmed) ? Number(trimmed) : NaN;
    if (!Number.isInteger(numeric) || numeric < min || numeric > max) {
      errors[field] = `${FIELD_LABELS[field]} must be an integer between ${min} and ${max}.`;
    } else {
      values[field] = numeric;
    }
  }
  return { errors, values };
}

function hasErrors(errors) {
  return Object.keys(errors).length > 0;
}

function formatEvidence(match) {
  const evidence = match.evidence;
  if (!evidence || !evidence.kind) return 'unknown';
  const parts = [evidence.kind];
  if (evidence.views !== null && evidence.views !== undefined) parts.push(`${evidence.views} views`);
  if (evidence.root_post_date) parts.push(`root ${evidence.root_post_date.slice(0, 10)}`);
  if (match.member_count !== null && match.member_count !== undefined) parts.push(`${match.member_count} members`);
  return parts.join(' · ');
}

const PreissueRelevanceManager = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [draft, setDraft] = useState({ enabled: false, values: { ...DEFAULTS } });
  const [saved, setSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const previewVersion = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      setFieldErrors({});
      try {
        const state = await projectService.getPreissueRelevanceSettings(projectId);
        if (cancelled) return;
        setDraft(draftFromState(state));
      } catch (error) {
        if (!cancelled) setLoadError(error?.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, reloadKey]);

  const invalidatePreview = () => {
    previewVersion.current += 1;
    setPreview(null);
    setPreviewError('');
  };

  const updateDraft = (changes) => {
    invalidatePreview();
    setSaved(null);
    setSaveError('');
    setDraft((current) => ({ ...current, ...changes }));
  };

  const updateValue = (event) => {
    const { name, value } = event.target;
    invalidatePreview();
    setSaved(null);
    setSaveError('');
    setFieldErrors((current) => {
      if (!current[name]) return current;
      const { [name]: _cleared, ...rest } = current;
      return rest;
    });
    setDraft((current) => ({ ...current, values: { ...current.values, [name]: value } }));
  };

  const save = async (event) => {
    event.preventDefault();
    let payload;
    if (draft.enabled) {
      const { errors, values } = validateThresholds(draft);
      setFieldErrors(errors);
      if (hasErrors(errors)) return;
      payload = { enabled: true, ...values };
    } else {
      setFieldErrors({});
      payload = { enabled: false };
    }
    setSaving(true);
    setSaveError('');
    setSaved(null);
    try {
      const state = await projectService.updatePreissueRelevanceSettings(projectId, payload);
      setDraft(draftFromState(state));
      setSaved(state.enabled === true
        ? 'Relevance settings saved. New keyword cards are now filtered before issue.'
        : 'Relevance settings saved. The filter is off and all keyword cards are issued.');
    } catch (error) {
      setSaveError(error?.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    const { errors, values } = validateThresholds(draft);
    setFieldErrors(errors);
    if (hasErrors(errors)) return;
    const version = previewVersion.current;
    setPreviewing(true);
    setPreviewError('');
    setPreview(null);
    try {
      const response = await projectService.previewPreissueRelevance(projectId, {
        enabled: draft.enabled,
        ...values,
      });
      if (previewVersion.current === version) setPreview(response);
    } catch (error) {
      if (previewVersion.current === version) {
        setPreviewError(error?.response?.data?.message || error.message);
      }
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading relevance settings..." />;
  }

  if (loadError) {
    return (
      <section className="mt-4" aria-label="Pre-issue relevance">
        <Alert variant="error" className="mb-3">{loadError}</Alert>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setReloadKey((key) => key + 1)}
        >
          Try again
        </Button>
      </section>
    );
  }

  const captionId = 'preissue-preview-caption';

  return (
    <section className="mt-4" aria-label="Pre-issue relevance">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-medium text-gray-900">Relevance before issue</h3>
        <p className="mt-1 text-xs text-gray-600">
          Filters keyword matches by chat size and root post popularity before they become
          Messenger cards. Disabled by default; turning the filter off clears the project setting.
        </p>

        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
          <p><span className="font-medium">Scope:</span> only new keyword cards in the Messenger &quot;New&quot;
            queue are filtered. Direct links, the work and archive queues, keyword matching itself
            and report-group forwarding are not affected.</p>
          <p className="mt-1">Unknown evidence is always rejected while the filter is enabled.</p>
        </div>

        <form onSubmit={save} noValidate>
          <div className="mt-3 flex items-start">
            <input
              type="checkbox"
              id="preissue-relevance-enabled"
              name="enabled"
              checked={draft.enabled}
              onChange={(event) => updateDraft({ enabled: event.target.checked })}
              aria-describedby="preissue-relevance-enabled-hint"
              className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="preissue-relevance-enabled" className="ml-2 block text-sm text-gray-900">
              Filter keyword cards before issue
            </label>
          </div>
          <p id="preissue-relevance-enabled-hint" className="mt-1 text-xs text-gray-500">
            When off, the project stores no filter setting and every keyword match is issued.
          </p>

          <fieldset className="mt-3">
            <legend className="text-xs font-medium text-gray-700">Thresholds</legend>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {Object.keys(FIELD_LABELS).map((field) => {
                const fieldError = fieldErrors[field];
                const describedBy = fieldError
                  ? `preissue-${field}-hint preissue-${field}-error`
                  : `preissue-${field}-hint`;
                return (
                  <div key={field}>
                    <label htmlFor={`preissue-${field}`} className="block text-xs font-medium text-gray-700">
                      {FIELD_LABELS[field]}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      id={`preissue-${field}`}
                      name={field}
                      value={draft.values[field]}
                      onChange={updateValue}
                      aria-describedby={describedBy}
                      aria-invalid={fieldError ? 'true' : 'false'}
                      className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                    />
                    <p id={`preissue-${field}-hint`} className="mt-1 text-xs text-gray-500">{FIELD_HINTS[field]}</p>
                    {fieldError && (
                      <p id={`preissue-${field}-error`} className="mt-1 text-xs text-red-600">{fieldError}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-4">
            <Button type="submit" variant="primary" isLoading={saving}>
              Save relevance settings
            </Button>
          </div>
        </form>

        <div aria-live="polite">
          {saveError && <Alert variant="error" className="mt-3">{saveError}</Alert>}
          {saved && !saveError && <Alert variant="success" className="mt-3">{saved}</Alert>}
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
        <h4 className="text-sm font-medium text-gray-900">Preview on recent matches</h4>
        <p className="mt-1 text-xs text-gray-600">
          Evaluates the current draft thresholds (nothing is saved) against the latest keyword
          matches of this project, using the same eligibility rule as the live queue.
        </p>
        <div className="mt-3">
          <Button type="button" variant="secondary" onClick={runPreview} isLoading={previewing}>
            Preview last matches
          </Button>
        </div>

        <div aria-live="polite">
          {previewError && <Alert variant="error" className="mt-3">{previewError}</Alert>}
        </div>

        {preview && (
          <div className="mt-4" aria-live="polite">
            {preview.matches.length === 0 ? (
              <p className="text-sm text-gray-600">No recent keyword matches for this project.</p>
            ) : (
              <>
                <p className="text-xs text-gray-700">
                  <span className="font-medium">
                    {`${preview.stats.eligible} of ${preview.sampled} recent matches eligible`}
                  </span>
                  {' · '}
                  {REASON_ORDER.map((reason, index) => (
                    <React.Fragment key={reason}>
                      {index > 0 && ' · '}
                      <span className="whitespace-nowrap">
                        {`${REASON_LABELS[reason]}: ${preview.stats.rejected[reason]}`}
                      </span>
                    </React.Fragment>
                  ))}
                </p>
                <div
                  role="region"
                  tabIndex={0}
                  aria-labelledby={captionId}
                  className="mt-3 max-h-96 overflow-auto rounded-md border border-gray-200"
                >
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <caption id={captionId} className="sr-only">
                      Preview of the latest {preview.sampled} keyword matches with eligibility result
                    </caption>
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matched</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Snippet</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Evidence</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {preview.matches.map((match) => (
                        <tr key={match.id}>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                            {match.matched_at ? new Date(match.matched_at).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-900">{match.snippet}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{formatEvidence(match)}</td>
                          <td className="px-3 py-2 text-xs">
                            {match.eligible
                              ? <Badge variant="green" size="sm">Eligible</Badge>
                              : <Badge variant="red" size="sm">Rejected: {REASON_LABELS[match.reason] || REASON_LABELS.unknown_evidence}</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PreissueRelevanceManager;
